import { BzDateFns } from '@breezy/shared'
import { Tooltip, type TooltipProps } from 'antd'
import classNames from 'classnames'
import React, { useMemo } from 'react'
import { useConnectivity } from '../../providers/ConnectivityProvider'
import './ConnectivityDot.less'

const STATUS_META = {
  healthy: { color: '#52c41a', label: 'Online' },
  degraded: { color: '#fa8c16', label: 'Degraded' },
  offline: { color: '#ff4d4f', label: 'Offline' },
} as const

type ConnectivityDotProps = {
  size?: number
  className?: string
  placement?: TooltipProps['placement']
  trigger?: TooltipProps['trigger']
  showTooltip?: boolean
  showStatusPill?: boolean
}

export const ConnectivityDot = ({
  size = 8,
  className,
  placement = 'bottomRight',
  trigger = ['hover', 'focus'],
  showTooltip = true,
  showStatusPill = false,
}: ConnectivityDotProps) => {
  const { status, lastSuccessAt, lastSuccessAtMs } = useConnectivity()

  const meta = STATUS_META[status]
  const showPill = showStatusPill && status !== 'healthy'

  const tooltipTitle = useMemo(() => {
    const reference = lastSuccessAtMs ?? (lastSuccessAt ? new Date(lastSuccessAt).getTime() : undefined)

    if (!reference) {
      return `${meta.label} — no successful heartbeat yet`
    }

    const distance = BzDateFns.formatDistanceToNowStrict(reference, { addSuffix: true })

    return `${meta.label} — last checked ${distance}`
  }, [lastSuccessAt, lastSuccessAtMs, meta.label])

  const dot = (
    <span
      role="status"
      aria-label={tooltipTitle}
      className={classNames('inline-block rounded-full', className)}
      style={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        backgroundColor: meta.color,
        boxShadow: '0 0 0 1px #fff',
      }}
    />
  )

  const renderedDot = showTooltip ? (
    <Tooltip title={tooltipTitle} placement={placement} trigger={trigger}>
      {dot}
    </Tooltip>
  ) : (
    dot
  )

  return (
    <span className="connectivity-indicator">
      {renderedDot}
      {showPill ? (
        <span key={status} className="connectivity-pill" style={{ backgroundColor: meta.color }} aria-live="polite">
          {meta.label}
        </span>
      ) : null}
    </span>
  )
}
